import QUERY_KEYS from 'network/config/queryKeys';
import React, { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { getProductsByVendorsandCategories } from '../../network/services/goods';

export default function useProductsByVendorsCategoriesHook() {
  const [searchParams] = useSearchParams();
  const LIMIT = 10;

  const category = searchParams.get('category');
  const vendor = searchParams.get('vendor');
  const locationId = searchParams.get('locationId'); // ✅ Extract locationId from URL

  const {
    isFetching: productsByVendorsisFetching,
    data: productsByVendorsData,
    hasNextPage: productsByVendorsHasNext,
    fetchNextPage: productsByVendorsFetchNext,
    isLoading: queryIsLoading,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.PRODUCT_BY_VENDORS_CATEGORIES, { categoryId: category, vendorId: vendor, locationId }], // ✅ Add locationId to query key
    enabled: !!(category && vendor),
    queryFn: ({ pageParam = 0 }) =>
      getProductsByVendorsandCategories({
        categoryId: category,
        vendorId: vendor,
        locationId: locationId, // ✅ Pass locationId to API call
        page: pageParam,
        size: LIMIT
      }),
    getNextPageParam: (lastPage, allPages) => {
      console.log('getNextPageParam called with:', {
        lastPage: lastPage ? { currentPage: lastPage.currentPage, totalPages: lastPage.totalPages } : null,
        pagesLength: allPages.length
      });

      if (lastPage && typeof lastPage === 'object') {
        const { currentPage, totalPages } = lastPage;

        // API uses 0-based indexing, so check if currentPage < totalPages - 1
        if (typeof currentPage === 'number' && typeof totalPages === 'number') {
          if (currentPage < totalPages - 1) {
            const nextPage = currentPage + 1;
            console.log('Returning next page:', nextPage);
            return nextPage;
          } else {
            console.log('Reached last page, no more pages');
            return undefined;
          }
        }
      }

      console.log('No valid pagination info found');
      return undefined;
    },
    // Performance optimizations
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  // Extract seller and subcategories from the first page of data
  const { seller, subCategories } = useMemo(() => {
    const firstPage = productsByVendorsData?.pages?.[0];

    if (firstPage && typeof firstPage === 'object') {
      return {
        seller: firstPage.seller || null,
        subCategories: firstPage.subCategories || []
      };
    }

    return {
      seller: null,
      subCategories: []
    };
  }, [productsByVendorsData?.pages]);

  // Memoize the fetchMore function to prevent unnecessary re-renders
  const fetchMoreProductsByVendors = React.useCallback(() => {
    console.log('fetchMoreProductsByVendors called', {
      hasNextPage: productsByVendorsHasNext,
      isFetchingNextPage,
      isFetching: productsByVendorsisFetching
    });

    if (productsByVendorsHasNext && !isFetchingNextPage && !productsByVendorsisFetching) {
      console.log('Fetching next page...');
      productsByVendorsFetchNext();
    }
  }, [productsByVendorsFetchNext, productsByVendorsHasNext, isFetchingNextPage, productsByVendorsisFetching]);

  return {
    productsByVendorsData,
    productsByVendorsisFetching,
    fetchMoreProductsByVendors,
    productsByVendorsHasNext,
    seller,
    subCategories,
    isInitialLoading: queryIsLoading && !productsByVendorsData?.pages?.length,
    isFetchingNextPage
  };
}